/**
 * The candidate's submitted information, grouped for read-only display.
 *
 * Used in two places that must agree:
 *   - the candidate re-opening their own link after submitting (their receipt)
 *   - the manager's "Candidate form" preview modal
 *
 * Both render the same groups in the same order, so what the manager reviews is
 * exactly what the candidate believes they sent.
 *
 * This is the PERSONAL DETAILS form's submission (step 3) — what the candidate
 * sent after a successful interview. What they sent BEFORE the interview (CV,
 * Right to Work documents, employment history, referees) is shown on step 1 by
 * recruitment/PreInterviewPanel.js and is deliberately not repeated here.
 */

/** "2026-08-15" / ISO timestamp → "15 Aug 2026". Falsy → ''. */
export const formatSummaryDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * JSON columns come back as arrays or objects; free-text ones as strings.
 * Render any of them as something a human can read.
 */
/** A next-of-kin object as one readable line. Handles the legacy bare string. */
const kinLine = (kin) => {
  if (!kin) return '';
  if (typeof kin === 'string') return kin;
  return [kin.name, kin.relationship, kin.phone].filter(Boolean).join(' · ');
};

export const toDisplayText = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join('\n');
  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n');
  }
  return String(value);
};

/**
 * Build the display groups.
 *
 * @param {object} input
 * @param {string} input.name    candidate's legal name (lives on the user row)
 * @param {string} input.email
 * @param {string} input.phone
 * @param {object} input.details the candidate-editable answers, by field name
 * @returns {Array<{title: string, fields: Array<{label, value, full}>}>}
 */
export const buildCandidateSummary = ({ name, email, phone, details = {} } = {}) => {
  const d = details || {};

  const group = (title, fields) => ({
    title,
    fields: fields.map(([label, value, full = false]) => ({
      label,
      value: toDisplayText(value),
      full,
    })),
  });

  // A group where every field is empty says nothing a reader can act on — it
  // just prints a column of dashes. That started happening as soon as emergency
  // contact moved to the onboarding link, so the candidate's own post-submit
  // summary showed an "Emergency contact" block they were never asked to fill.
  const nonEmpty = (groups) => groups.filter(
    (g) => g.fields.some((field) => String(field.value ?? '').trim() !== ''),
  );

  return nonEmpty([
    group('Personal & identity', [
      ['Legal full name', name],
      ['Preferred name', d.preferred_name],
      ['Date of birth', formatSummaryDate(d.dob)],
      ['National Insurance number', d.ni_number],
      ['Nationality', d.nationality],
      ['Gender', d.gender],
      ['Ethnicity', d.ethnicity],
      ['Disability / adjustments', d.disability_status, true],
    ]),
    group('Contact & address', [
      ['Email', email],
      ['Telephone', phone],
      ['Address line 1', d.address_line1],
      ['Address line 2', d.address_line2],
      ['Town or city', d.address_town],
      ['Postcode', d.address_postcode],
      ['Country', d.address_country],
      ['Current address', d.current_address, true],
      ['Previous address', d.previous_addresses, true],
    ]),
    group('Emergency contact & next of kin', [
      ['Emergency contact', d.emergency_contact_name],
      ['Relationship', d.emergency_contact_rel],
      ['Emergency telephone', d.emergency_contact_phone],
      // The generic object renderer would print "name: …" one key per line.
      // A person reads better as a sentence.
      ['Next of kin', kinLine(d.next_of_kin), true],
    ]),
    // Employment terms and compliance reference numbers are deliberately
    // absent. The form does not ask for either — the terms are the employer's
    // to set, and the Right to Work and DBS documents were sent and read before
    // the interview — so listing them as "what the candidate sent" would be a
    // misrepresentation, whatever happens to be stored in those columns.
    group('Payroll', [
      ['Name on the bank account', d.bank_account_name],
      ['Sort code', d.bank_sort_code],
      ['Account number', d.bank_account_number],
      ['Building society roll number', d.bank_building_society_ref],
    ]),
  ]);
};

export default buildCandidateSummary;
